// One „*” rule for every range (PR2, 2026-10-29): an amount is marked when a
// ZK in ITS range (month, quarter, year, pivot cell, customer row, list) still
// has an incomplete cost AFTER that range is summed — the rule the backend
// applies to the range it was asked for (`pendingCostOrderCount`). A ZK
// invoiced in October before its goods were issued and completed by the WZ in
// November is marked in October alone, not in October–November.
//
// The ledger sends the change of pending invoice lines per month and invoice
// (`pendingDeltas` on a ZK row, `pendingCostDeltas` on a month line, keyed
// `orderId|invoiceId`); an invoice whose changes sum above zero in the range
// is still pending there. A forecast (plan) part without an average cost is a
// state, not a change: it marks every range it is in. Data without deltas
// (older responses, fixtures) keeps the per-month flag.

export type PendingDeltas = Record<string, number>;

export interface PendingScope {
  /** orderId → invoiceId → summed change of pending lines in the scope. */
  ledger: Map<string, Map<string, number>>;
  /** Orders marked by a state (plan without an average, or a flag without deltas). */
  sticky: Set<string>;
}

export function emptyPendingScope(): PendingScope {
  return { ledger: new Map(), sticky: new Set() };
}

const EPSILON = 1e-9;

/**
 * Adds one ledger (actual) month of an order. `deltas` = invoice → change; when
 * absent, `pendingFlag` (the month's own flag) marks the order as before.
 */
export function addLedgerMonth(scope: PendingScope, orderId: string, deltas: PendingDeltas | undefined, pendingFlag: boolean): void {
  if (!deltas) {
    if (pendingFlag) scope.sticky.add(orderId);
    return;
  }
  const invoices = scope.ledger.get(orderId) ?? new Map<string, number>();
  Object.entries(deltas).forEach(([invoice, delta]) => invoices.set(invoice, (invoices.get(invoice) ?? 0) + delta));
  scope.ledger.set(orderId, invoices);
}

/** A forecast (plan) part with an incomplete cost: marks the scope. */
export function addPlanPending(scope: PendingScope, orderId: string): void {
  scope.sticky.add(orderId);
}

/**
 * Adds a month line (`orderId|invoiceId` keys). Without `pendingCostDeltas`
 * the line's own count marks the scope (`fallbackKey` keeps it distinct).
 */
export function addLineDeltas(scope: PendingScope, deltas: PendingDeltas | undefined, fallbackCount: number, fallbackKey: string): void {
  if (!deltas) {
    for (let index = 0; index < fallbackCount; index += 1) scope.sticky.add(`${fallbackKey}#${index}`);
    return;
  }
  Object.entries(deltas).forEach(([key, delta]) => {
    const split = key.indexOf('|');
    const orderId = split >= 0 ? key.slice(0, split) : key;
    const invoice = split >= 0 ? key.slice(split + 1) : 'none';
    addLedgerMonth(scope, orderId, { [invoice]: delta }, false);
  });
}

export function mergePendingScope(target: PendingScope, source: PendingScope): void {
  source.sticky.forEach((id) => target.sticky.add(id));
  source.ledger.forEach((invoices, orderId) => {
    const into = target.ledger.get(orderId) ?? new Map<string, number>();
    invoices.forEach((delta, invoice) => into.set(invoice, (into.get(invoice) ?? 0) + delta));
    target.ledger.set(orderId, into);
  });
}

/** Orders still incomplete after the scope is summed. */
export function pendingOrderIds(scope: PendingScope): Set<string> {
  const ids = new Set(scope.sticky);
  scope.ledger.forEach((invoices, orderId) => {
    for (const delta of Array.from(invoices.values())) {
      if (delta > EPSILON) {
        ids.add(orderId);
        break;
      }
    }
  });
  return ids;
}

export function isScopePending(scope: PendingScope): boolean {
  return pendingOrderIds(scope).size > 0;
}

/** Sum of two delta maps (a ZK's months folded into one part). */
export function sumDeltas(a: PendingDeltas | undefined, b: PendingDeltas | undefined): PendingDeltas | undefined {
  if (!a && !b) return undefined;
  const result: PendingDeltas = { ...(a ?? {}) };
  Object.entries(b ?? {}).forEach(([key, delta]) => { result[key] = (result[key] ?? 0) + delta; });
  return result;
}
